import _ from 'lodash';

import { ADMIN_PANEL_CLIENT_ID } from 'constants/auth';
import { USER_SEARCH_MAX_IDS } from 'constants/userSearch';

export class UsersManagementApi {
  #http;

  constructor(http) {
    this.#http = http;
  }

  async getUsers(params) {
    return this.#http.get('api/users', {
      headers: { clientId: ADMIN_PANEL_CLIENT_ID },
      params,
      paramsSerializer: { indexes: true },
    });
  }

  async searchUsers(body, config = {}) {
    return this.#http.post('api/users/search', body, {
      ...config,
      headers: { clientId: ADMIN_PANEL_CLIENT_ID },
    });
  }

  // The endpoint rejects an ids filter above USER_SEARCH_MAX_IDS with a typed 400,
  // so the cap is handled here rather than at each call site. Returns the merged items.
  async searchUsersByIds({ companyId, ids, fields, deletedPolicy }) {
    const responses = await Promise.all(
      _.chunk(ids, USER_SEARCH_MAX_IDS).map(idsChunk =>
        this.searchUsers({ fields, filter: { companyId, ids: idsChunk, deletedPolicy } }),
      ),
    );

    return responses.flatMap(({ data }) => data.items);
  }

  async getUser({ companyId, id }) {
    return this.#http.get(`/api/companies/${companyId}/users/${id}`, {
      headers: { clientId: ADMIN_PANEL_CLIENT_ID },
    });
  }

  async editUser({ companyId, ...body }) {
    return this.#http.put(`api/companies/${companyId}/users/${body.id}`, body, {
      headers: { clientId: ADMIN_PANEL_CLIENT_ID },
    });
  }

  async createUser({ companyId, password, ...body }) {
    return this.#http.post(`/api/companies/${companyId}/users`, body, {
      headers: {
        clientId: ADMIN_PANEL_CLIENT_ID,
        password,
      },
    });
  }

  async deleteUser({ companyId, id }) {
    return this.#http.delete(`/api/companies/${companyId}/users/${id}`, {
      headers: { clientId: ADMIN_PANEL_CLIENT_ID },
    });
  }

  async updateUserProperties({ userId, properties }) {
    return this.#http.put(`api/users/${userId}/properties`, properties, {
      headers: { clientId: ADMIN_PANEL_CLIENT_ID },
    });
  }

  async generateAppSecret({ companyId, userId }) {
    return this.#http.post(
      `/api/companies/${companyId}/users/${userId}/generate-app-secret`,
      null,
      {
        headers: { clientId: ADMIN_PANEL_CLIENT_ID },
      },
    );
  }

  // can replace single and multiple role assigment
  async createUserRoleAssignmentsV2(companyId, body) {
    return this.#http.post(`/api/companies/${companyId}/role-assignments`, body, {
      headers: { clientId: ADMIN_PANEL_CLIENT_ID },
    });
  }

  async createUserRoleAssignments({ companyId, ...body }) {
    return this.#http.post(
      `/api/companies/${companyId}/users/${body.userId}/role-assignments`,
      body,
      {
        headers: { clientId: ADMIN_PANEL_CLIENT_ID },
      },
    );
  }

  async createUserRoleAssignmentsMultiple({ companyId, data }) {
    return this.#http.post(`/api/companies/${companyId}/role-assignments`, data, {
      headers: { clientId: ADMIN_PANEL_CLIENT_ID },
    });
  }

  // can replace single and multiple role removing
  async deleteUserRoleAssignmentsV2(companyId, data) {
    return this.#http.delete(`/api/companies/${companyId}/role-assignments`, {
      data,
      headers: { clientId: ADMIN_PANEL_CLIENT_ID },
    });
  }

  async deleteUserRoleAssignments({ companyId, userId, roleId }) {
    return this.#http.delete(
      `/api/companies/${companyId}/users/${userId}/role-assignments/${roleId}`,
      {
        headers: { clientId: ADMIN_PANEL_CLIENT_ID },
      },
    );
  }

  async deleteUserRoleAssignmentsMultiple({ companyId, data }) {
    return this.#http.delete(`/api/companies/${companyId}/role-assignments`, {
      headers: { clientId: ADMIN_PANEL_CLIENT_ID },
      data,
    });
  }
}
